import { Users, DoorClosed, Presentation, Video, Laptop, GraduationCap, Coffee, ArrowUpRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import sharedOfficeImg from "@/assets/cat-shared-office.webp";
import privateOfficeImg from "@/assets/cat-private-office.webp";
import meetingRoomImg from "@/assets/cat-meeting-room.webp";
import conferenceRoomImg from "@/assets/cat-conference-room.webp";
import coworkingImg from "@/assets/cat-coworking-space.webp";
import trainingRoomImg from "@/assets/cat-training-room.webp";
import hotDeskImg from "@/assets/cat-hot-desk.webp";

export const spaceCategories = [
  { slug: "shared-office", name: "Shared Office", count: 1240, icon: Users, image: sharedOfficeImg },
  { slug: "private-office", name: "Private Office", count: 980, icon: DoorClosed, image: privateOfficeImg },
  { slug: "meeting-room", name: "Meeting Room", count: 1530, icon: Presentation, image: meetingRoomImg },
  { slug: "conference-room", name: "Conference Room", count: 640, icon: Video, image: conferenceRoomImg },
  { slug: "coworking-space", name: "Coworking Space", count: 2100, icon: Laptop, image: coworkingImg },
  { slug: "training-room", name: "Training Room", count: 480, icon: GraduationCap, image: trainingRoomImg },
  { slug: "hot-desk", name: "Hot Desk", count: 3240, icon: Coffee, image: hotDeskImg },
];

export function Categories() {
  const { t, i18n } = useTranslation();
  const shown = spaceCategories.slice(0, 4);
  const nf = new Intl.NumberFormat(i18n.language === "ar" ? "ar-SA" : "en-US");
  return (
    <section id="categories" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
        <div className="max-w-xl">
          <div className="text-xs font-semibold uppercase tracking-widest text-primary">{t("categories.eyebrow")}</div>
          <h2 className="mt-3 font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("categories.title")}</h2>
        </div>
        <Link to="/categories" className="inline-flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary">
          {t("categories.seeAll")} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {shown.map((cat) => (
          <Link
            key={cat.slug}
            to="/discover"
            search={{ category: cat.slug } as never}
            className="group relative overflow-hidden rounded-3xl border border-border bg-card shadow-soft transition-all hover:-translate-y-1 hover:shadow-elevated"
          >
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={cat.image}
                alt={t(`categories.items.${cat.slug}`)}
                width={800}
                height={1000}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            </div>

            <div className="absolute start-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-card/95 text-primary shadow-soft backdrop-blur">
              <cat.icon className="h-4.5 w-4.5" />
            </div>

            <div className="absolute inset-x-4 bottom-4 flex items-end justify-between text-white">
              <div>
                <div className="font-display text-xl font-bold">{t(`categories.items.${cat.slug}`)}</div>
                <div className="text-xs text-white/80">{nf.format(cat.count)} {t("categories.spaces")}</div>
              </div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-all group-hover:bg-white group-hover:text-foreground">
                <ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
